import React, { useState } from 'react';
import { BaseTool, IToolManifest, IToolComponentProps, IToolContext } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { Frame, Layout, Layers, Grid3x3, PanelTop } from 'lucide-react';
import { cn } from '../../lib/utils';

const frameTypes = [
  { 
    id: 'basic', 
    label: 'Basic Frame', 
    icon: Frame,
    description: 'Simple container frame'
  },
  { 
    id: 'section', 
    label: 'Section', 
    icon: PanelTop,
    description: 'Labeled section frame'
  },
  { 
    id: 'board', 
    label: 'Board', 
    icon: Layout,
    description: 'Kanban-style board'
  },
  { 
    id: 'grid', 
    label: 'Grid', 
    icon: Grid3x3,
    description: 'Grid layout frame'
  },
  { 
    id: 'layers', 
    label: 'Layers', 
    icon: Layers,
    description: 'Layered frame stack'
  },
];

const FramesToolComponent: React.FC<IToolComponentProps> = ({ onExecute }) => {
  const [open, setOpen] = useState(false);
  
  const handleFrameSelect = (frameType: string) => {
    onExecute({ frameType });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="tid.tools.frames.trigger"
          className="w-10 h-10 rounded-lg"
          onClick={() => handleFrameSelect('basic')}
        >
          <Frame className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[280px] p-2" 
        align="start"
        side="top"
        sideOffset={8}
      >
        <div className="space-y-1">
          {frameTypes.map((frame) => {
            const Icon = frame.icon;
            return (
              <button
                key={frame.id}
                data-testid={`tid.tools.frames.type.${frame.id}`}
                onClick={() => handleFrameSelect(frame.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2",
                  "rounded-lg hover:bg-[#F3F4F6]",
                  "transition-colors text-left"
                )}
              >
                <Icon className="h-5 w-5 text-[#475569] flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0F172A]">
                    {frame.label}
                  </div>
                  <div className="text-xs text-[#94A3B8]">
                    {frame.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export class FramesTool extends BaseTool {
  private selectedFrameType = 'basic';

  manifest: IToolManifest = {
    id: 'frames',
    name: 'Frames',
    icon: 'frame',
    category: 'create',
    description: 'Add frames and containers',
    version: '1.0.0',
    
    shortcuts: [
      {
        key: 'F',
        modifiers: [],
        description: 'Activate frame tool'
      }
    ],
    
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: false,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    }
  };

  canExecute(context: IToolContext): boolean {
    if (context.diagramType === 'genogram') return false;
    return super.canExecute(context);
  }

  onActivate(context: IToolContext): void {
    super.onActivate(context);
    // No direct drawing here; delegate to legacy genogram frame tool via event bus on execute
  }

  onDeactivate(): void {
    // Nothing to clean up; legacy tool manages cursor/events
    super.onDeactivate();
  }

  execute(params?: { frameType: string }): ICommand | null {
    // Store selected frame type for drag-to-draw
    if (params?.frameType) {
      this.selectedFrameType = params.frameType;
    }
    
    // Delegate creation to legacy genogram frame tool for 'basic' (previous implementation)
    // Emit event that the canvas listens to, to activate 'genogram-frame'
    if (this.context?.eventBus) {
      this.context.eventBus.emit('frame:tool-activate', { type: this.selectedFrameType });
    }
    // Do not return a command; legacy tool handles creation
    return null;
  }

  getToolbarComponent() {
    return FramesToolComponent;
  }
}
